/**
 * Modul "Benutzeraktivitäten": vollständiges Protokoll aller Vorgänge im
 * System (Login/Logout, Benutzer- und Rechteänderungen, Rollenänderungen,
 * Passwortänderungen, Lagerbewegungen, Kassenvorgänge, Artikeländerungen,
 * administrative Änderungen). Nur mit der Berechtigung "activity.view"
 * sichtbar - Zugriffsschutz erfolgt zusätzlich serverseitig in
 * backend/src/routes/activity.ts, diese Seite ist also auch bei direktem
 * Aufruf der URL für nicht berechtigte Benutzer ohne Wirkung (ProtectedRoute
 * blendet aus, die API liefert zusätzlich 403).
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { Card } from "../../components/ui/Card";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { formatDateTime } from "../../utils/formatDate";
import type { AuditLogEntry, PaginatedResult } from "../../types";

export default function UserActivity() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const pageSize = 50;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (action) params.set("action", action);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    api.get<PaginatedResult<AuditLogEntry>>(`/api/activity?${params}`)
      .then((r) => { setEntries(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, [page, action, from, to]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-gray-100">Benutzeraktivitäten</h1>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <input className="input sm:max-w-xs" placeholder="Aktion filtern, z.B. product.update" value={action} onChange={(e) => { setPage(1); setAction(e.target.value); }} />
          <div><label className="label">Von</label><input type="date" className="input" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} /></div>
          <div><label className="label">Bis</label><input type="date" className="input" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} /></div>
        </div>
      </Card>

      <Card padded={false} className="overflow-x-auto">
        {loading ? <Spinner /> : entries.length === 0 ? <EmptyState message="Keine Einträge gefunden." /> : (
          <table className="table-base">
            <thead><tr><th>Zeitpunkt</th><th>Benutzer</th><th>Aktion</th><th>Beschreibung</th></tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="text-gray-400 whitespace-nowrap">{formatDateTime(e.created_at)}</td>
                  <td className="text-gray-200">{e.username}</td>
                  <td className="text-gray-500 font-mono text-xs">{e.action}</td>
                  <td className="text-gray-300">{e.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button className="btn-secondary px-3 py-1" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Zurück</button>
          <span className="text-gray-400">Seite {page} von {totalPages}</span>
          <button className="btn-secondary px-3 py-1" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Weiter</button>
        </div>
      )}
    </div>
  );
}
